import React, { FC, MouseEvent, useEffect, useRef, useState } from "react";

import { Box, CircularProgress, Paper, Tooltip } from "@mui/material";
import { useDebouncedCallback } from "use-debounce";
import { useSnackbar } from "notistack";
import { useIntl } from "react-intl";
import { getDaysInMonth, setDate } from "date-fns";

import { ApiError } from "@framework/provider-api-jwt";
import { Precision, RangeType, useAsu } from "@framework/provider-asu";

import {
  useGetTooltip,
  useGetVisualTooltipTrain,
  useGetVisualComments,
} from "../../../../pages/dashboard/tabs/journal/api-hooks";
import { useAlarmPaletteResolver } from "../../../../hooks/use-alarm-color";
import {
  IRequestReport,
  IResponseVisualTrainComments,
  IComment,
} from "../../../../pages/dashboard/tabs/journal/interfaces";
import { VISUAL_COMMENT_TYPE_SYMBOL, TCommentTypeSymbol } from "../../../../pages/dashboard/tabs/journal/comment-types";
import { toISOStringJournal } from "../../../../utils";
import { useFormatTooltip, TPointsTooltip } from "../../utils";
import { StyledTooltip } from "../tooltip";
import { CommentDetailsDialog } from "../../../dialogs/journal-comments/comment-details-dialog";
import { CommentListDialog } from "../../../dialogs/journal-comments/comment-list-dialog";

interface ITrainId {
  trainId?: string | number;
}

interface IStyledAlarmCellProps extends ITrainId {
  status?: string | number | null; // `pointsStatus/visualStatus`
  dateStr: string;
  precision: Precision;
  row: any;
  commentTypeSymbol: TCommentTypeSymbol;
  valueType?: number;
  paramId: string;
  onReportDownload: (dto: IRequestReport) => () => Promise<void>;
  setLoading: (value: boolean) => void;
}

type AlarmCellStatus = IStyledAlarmCellProps["status"];

const EMPTY_CELL_COLOR = "#FFFFFF";
const EMPTY_VISUAL_CONTROL_COLOR = "#f6f6f6";

const hasAlarmCellStatus = (status: AlarmCellStatus): boolean =>
  status !== null && status !== undefined && status !== "";

const getAlarmCellStatuses = (status: AlarmCellStatus): [number, number] => {
  const statusValue = hasAlarmCellStatus(status) ? String(status) : "0";
  const [pointsStatus, visualStatus = "0"] = statusValue.split("/");

  return [Number(pointsStatus), Number(visualStatus)];
};

export const StyledAlarmCell: FC<IStyledAlarmCellProps> = props => {
  const {
    status,
    trainId,
    dateStr,
    precision,
    row,
    commentTypeSymbol,
    valueType,
    paramId,
    onReportDownload,
    setLoading,
    ...rest
  } = props;

  const { selectedItem, updateQS, isVisualControlOpen } = useAsu();

  const selectedTrainId =
    selectedItem && "TrainId" in selectedItem && selectedItem.TrainId ? selectedItem.TrainId : null;

  const hasStatus = hasAlarmCellStatus(status);
  const [pointsStatus, visualStatus] = getAlarmCellStatuses(status);

  const resolveAlarmColor = useAlarmPaletteResolver();
  const pointsColor = resolveAlarmColor(Number(pointsStatus) || 0);
  const visualColor = resolveAlarmColor(Number(visualStatus) || 0);
  const isSplitVisualCell = Boolean(trainId) && Number.isFinite(visualStatus);

  const [pointsTooltip, setPointsTooltip] = useState<TPointsTooltip | null>(null);
  const [pointsOpen, setPointsOpen] = useState<boolean>(false);
  const [pointsError, setPointsError] = useState<string | null>(null);

  const [visualTooltipTrain, setVisualTooltipTrain] = useState<TPointsTooltip | null>(null);
  const [visualTooltipPoints, setVisualTooltipPoints] = useState<string>("");
  const [visualOpen, setVisualOpen] = useState<boolean>(false);
  const [visualError, setVisualError] = useState<string | null>(null);

  const [openCommentListDialog, setOpenCommentListDialog] = useState(false);
  const [commentListDialogData, setCommentListDialogData] = useState<IResponseVisualTrainComments | null>(null);
  const [openCommentDetailsDialog, setOpenCommentDetailsDialog] = useState(false);
  const [commentDetailsDialogData, setCommentDetailsDialogData] = useState<IResponseVisualTrainComments | null>(null);
  const [isCommentListDialogDataLoaded, setIsCommentListDialogDataLoaded] = useState(false);
  const [isCommentDetailsDialogDataLoaded, setIsCommentDetailsDialogDataLoaded] = useState(false);

  const { formatMessage } = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const { formatTrainTooltip, formatMeasureTooltip, formatVisualTooltipTrain, formatVisualTooltipPoints } =
    useFormatTooltip();

  const abortControllerPoints = useRef(new AbortController());
  const abortControllerVisualTrain = useRef(new AbortController());
  const abortControllerDialogData = useRef(new AbortController());
  const abortControllerVisualTooltipData = useRef(new AbortController());

  const { fn: getDialogData, isLoading: isLoadingDialogData } = useGetVisualComments({
    abortControllerRef: abortControllerDialogData,
  });
  const { fn: getVisualTooltipData, isLoading: isLoadingVisualTooltipData } = useGetVisualComments({
    abortControllerRef: abortControllerVisualTooltipData,
  });
  const { getTooltip, isLoading } = useGetTooltip({ abortControllerRef: abortControllerPoints });
  const { getTooltip: fetchVisualTooltipTrain, isLoading: isVisualLoadingTrain } = useGetVisualTooltipTrain({
    abortControllerRef: abortControllerVisualTrain,
  });

  const date = new Date(dateStr);

  const { endTimestamp, startTimestamp } = {
    startTimestamp: dateStr,
    endTimestamp: toISOStringJournal(
      new Date(precision === Precision.month ? setDate(date, getDaysInMonth(date)) : dateStr),
      {
        postfix: "end",
      },
    ),
  };

  const handleDialogRowClick = (comment: IComment) => {
    setCommentDetailsDialogData({
      ...commentListDialogData!,
      comments: [comment],
    });
    setOpenCommentDetailsDialog(true);
  };

  const getCommentsDialogData = useDebouncedCallback(async () => {
    const commentsParamId = row?.isJournalComments ? selectedTrainId : paramId;
    if (!commentsParamId) return;
    await getDialogData(undefined, {
      date,
      precision,
      paramId: commentsParamId,
    })
      .then(json => {
        if (!json) return;
        setCommentListDialogData(json);
        if (commentTypeSymbol && commentTypeSymbol !== VISUAL_COMMENT_TYPE_SYMBOL.mixed) {
          setCommentDetailsDialogData(json);
        }
      })
      .catch((error: ApiError) => {
        if (error instanceof DOMException && error.name === "AbortError") {
          return;
        }
        setPointsError(formatMessage({ id: "pages.journals.reports.tooltipError" }));
        if (error.status === 400) {
          const errors = error.getLocalizedValidationErrors ? error.getLocalizedValidationErrors() : {};
          Object.keys(errors).forEach(key => {
            enqueueSnackbar(formatMessage({ id: errors[key] }, { label: key }), { variant: "error" });
          });
        } else {
          console.error("unknown error", error);
          enqueueSnackbar(error.message || formatMessage({ id: "snackbar.error" }), { variant: "error" });
        }
      });
  }, 500);

  const downloadReport = onReportDownload({
    idTrain: trainId,
    startTimestamp,
    endTimestamp,
  });

  const handleClick = async (event: MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();

    getCommentsDialogData.cancel();
    abortControllerDialogData.current.abort();

    abortControllerDialogData.current = new AbortController();

    if (!hasStatus && !commentTypeSymbol) {
      return;
    }

    if (props.row && props.row.paramName && pointsStatus && pointsStatus > 30 && !commentTypeSymbol) {
      return;
    }

    if (commentTypeSymbol === VISUAL_COMMENT_TYPE_SYMBOL.mixed) {
      if (!isCommentListDialogDataLoaded || commentListDialogData === null) {
        await getCommentsDialogData();
        setIsCommentListDialogDataLoaded(true);
      }
      setOpenCommentListDialog(true);
      return;
    }

    if (commentTypeSymbol) {
      if (!isCommentDetailsDialogDataLoaded || commentDetailsDialogData === null) {
        await getCommentsDialogData();
        setIsCommentDetailsDialogDataLoaded(true);
      }
      setOpenCommentDetailsDialog(true);
      return;
    }

    if (pointsStatus && pointsStatus > 30) {
      if (trainId) {
        await downloadReport();
      } else {
        setLoading(true);
        const tooltips = await getTooltip(undefined, {
          startTimestamp,
          endTimestamp,
          trainId: selectedTrainId,
        });
        setLoading(false);

        const tooltip = tooltips?.find(({ Direction, NamePoint }) => {
          const namePoint = `${NamePoint}${
            Direction ? ` ${formatMessage({ id: `enums.pointDirection.${Direction}` })}` : ""
          }`;
          return row.name === namePoint;
        });

        const analyticsUpdateQS: any = {
          idObject: row.id,
          object: 3,
          tab: 1,
          idParameter: tooltip?.NameMeasure,
          startTimestamp,
          endTimestamp,
          rangeType: RangeType.SELECTED_RANGE,
          scrollToAnalytics: true,
        };

        updateQS(analyticsUpdateQS);
      }
    }
  };

  const getPointsTooltip = useDebouncedCallback(async () => {
    if (!trainId && !selectedTrainId) return;

    await getTooltip(undefined, {
      startTimestamp,
      endTimestamp,
      trainId: trainId || selectedTrainId,
    })
      .then(json => {
        if (!json) return;
        if (selectedTrainId) {
          setPointsTooltip(formatMeasureTooltip(json, row.name));
        } else {
          setPointsTooltip(formatTrainTooltip(json));
        }
      })
      .catch((error: ApiError) => {
        if (error instanceof DOMException && error.name === "AbortError") {
          return;
        }
        setPointsError(formatMessage({ id: "pages.journals.reports.tooltipError" }));
        if (error.status === 400) {
          const errors = error.getLocalizedValidationErrors ? error.getLocalizedValidationErrors() : {};
          Object.keys(errors).forEach(key => {
            enqueueSnackbar(formatMessage({ id: errors[key] }, { label: key }), { variant: "error" });
          });
        } else {
          console.error("unknown error", error);
          enqueueSnackbar(error.message || formatMessage({ id: "snackbar.error" }), { variant: "error" });
        }
      });
  }, 500);

  const getVisualTooltipTrain = useDebouncedCallback(async () => {
    if (!trainId) return;
    await fetchVisualTooltipTrain(undefined, {
      date,
      precision,
      trainId,
    })
      .then(json => {
        if (!json) return;

        setVisualTooltipTrain(formatVisualTooltipTrain(json));
      })
      .catch((error: ApiError) => {
        if (error instanceof DOMException && error.name === "AbortError") {
          return;
        }
        setVisualError(formatMessage({ id: "pages.journals.reports.tooltipError" }));
        if (error.status === 400) {
          const errors = error.getLocalizedValidationErrors ? error.getLocalizedValidationErrors() : {};
          Object.keys(errors).forEach(key => {
            enqueueSnackbar(formatMessage({ id: errors[key] }, { label: key }), { variant: "error" });
          });
        } else {
          console.error("unknown error", error);
          enqueueSnackbar(error.message || formatMessage({ id: "snackbar.error" }), { variant: "error" });
        }
      });
  }, 500);

  const getVisualTooltipPoints = useDebouncedCallback(async () => {
    const commentsParamId = row?.isJournalComments ? selectedTrainId : paramId;
    if (!commentsParamId) return;
    await getVisualTooltipData(undefined, {
      date,
      precision,
      paramId: commentsParamId,
    })
      .then(json => {
        if (!json) return;
        setVisualTooltipPoints(formatVisualTooltipPoints(json));
      })
      .catch((error: ApiError) => {
        if (error instanceof DOMException && error.name === "AbortError") {
          return;
        }
        setVisualError(formatMessage({ id: "pages.journals.reports.tooltipError" }));
        if (error.status === 400) {
          const errors = error.getLocalizedValidationErrors ? error.getLocalizedValidationErrors() : {};
          Object.keys(errors).forEach(key => {
            enqueueSnackbar(formatMessage({ id: errors[key] }, { label: key }), { variant: "error" });
          });
        } else {
          console.error("unknown error", error);
          enqueueSnackbar(error.message || formatMessage({ id: "snackbar.error" }), { variant: "error" });
        }
      });
  }, 500);

  const onPointsMouseOver = async () => {
    if (!trainId && !selectedTrainId) {
      return;
    }

    if (pointsStatus && pointsStatus > 30 && props.row && props.row.name) {
      setPointsOpen(true);
      if (!pointsTooltip) {
        await getPointsTooltip();
      }
    }
  };

  const onPointsMouseOut = () => {
    getPointsTooltip.cancel();
    if (!abortControllerPoints.current.signal.aborted) {
      abortControllerPoints.current.abort();
    }
    abortControllerPoints.current = new AbortController();
    setPointsOpen(false);
    setPointsError(null);
  };

  const onVisualMouseOverTrain = async () => {
    if (!trainId && !selectedTrainId) {
      return;
    }

    if (visualStatus && visualStatus > 30 && !commentTypeSymbol) {
      setVisualOpen(true);
      if (!visualTooltipTrain) {
        await getVisualTooltipTrain();
      }
    }
  };

  const onVisualMouseOutTrain = () => {
    if (!abortControllerVisualTrain.current.signal.aborted) {
      getVisualTooltipTrain.cancel();
      abortControllerVisualTrain.current.abort();
    }
    setVisualOpen(false);
    setVisualError(null);
  };

  const onVisualMouseOverPoints = async () => {
    if (!paramId) {
      return;
    }
    if (commentTypeSymbol === VISUAL_COMMENT_TYPE_SYMBOL.mixed) {
      return;
    }

    if (props.row && props.row.name && pointsStatus && pointsStatus > 30) {
      setPointsOpen(true);
      if (!pointsTooltip) {
        await getPointsTooltip();
      }
    }

    if (commentTypeSymbol && (props.row?.paramName || props.row?.isJournalComments)) {
      setVisualOpen(true);
      if (!visualTooltipPoints) {
        await getVisualTooltipPoints();
      }
    }
  };

  const onVisualMouseOutPoints = () => {
    getPointsTooltip.cancel();
    if (!abortControllerPoints.current.signal.aborted) {
      abortControllerPoints.current.abort();
    }
    abortControllerPoints.current = new AbortController();
    getVisualTooltipPoints.cancel();
    if (!abortControllerVisualTooltipData.current.signal.aborted) {
      abortControllerVisualTooltipData.current.abort();
    }
    abortControllerVisualTooltipData.current = new AbortController();
    setPointsOpen(false);
    setPointsError(null);
    setVisualOpen(false);
    setVisualError(null);
  };

  const handleCellClick = (event: MouseEvent) => {
    void handleClick(event);
  };

  const handlePointsMouseOver = () => {
    void onPointsMouseOver();
  };

  const handleVisualMouseOverTrain = () => {
    void onVisualMouseOverTrain();
  };

  const handleVisualMouseOverPoints = () => {
    void onVisualMouseOverPoints();
  };

  const handleCommentListDialogClose = () => {
    setOpenCommentListDialog(false);
    getCommentsDialogData.cancel();
    abortControllerDialogData.current.abort();
    abortControllerDialogData.current = new AbortController();
  };

  const handleCommentDetailsDialogClose = () => {
    setOpenCommentDetailsDialog(false);
    setCommentDetailsDialogData(null);
    getCommentsDialogData.cancel();
    abortControllerDialogData.current.abort();
    abortControllerDialogData.current = new AbortController();
  };

  useEffect(() => {
    setPointsTooltip(null);
    setVisualTooltipTrain(null);
    setPointsError(null);
    setVisualError(null);
  }, [isVisualControlOpen]);

  if (!hasStatus && !commentTypeSymbol) {
    const emptyBackgroundColor = props.row?.paramName ? EMPTY_VISUAL_CONTROL_COLOR : EMPTY_CELL_COLOR;

    return (
      <Box
        onClick={handleCellClick}
        sx={{
          width: "100%",
          height: "100%",
          backgroundColor: emptyBackgroundColor,
        }}
      />
    );
  }

  return (
    <>
      <Tooltip
        title={
          <Paper
            elevation={1}
            sx={{
              m: 0,
              p: 1.5,
              display: "flex",
              alignItems: "center",
              flexDirection: "column",
              justifyContent: "center",
              backgroundColor: "#ffffff",
              wordBreak: "break-word",
            }}
          >
            {pointsError ||
              (isLoading && !visualTooltipTrain ? (
                <CircularProgress size={15} />
              ) : (
                pointsTooltip && <StyledTooltip data={pointsTooltip} />
              ))}
            {visualError ||
              (isVisualLoadingTrain ? (
                <CircularProgress size={15} />
              ) : (
                visualTooltipTrain && <StyledTooltip data={visualTooltipTrain} />
              ))}
            {visualError ||
              (isLoadingVisualTooltipData ? (
                <CircularProgress size={15} />
              ) : (
                visualTooltipPoints && <StyledTooltip data={visualTooltipPoints} />
              ))}
          </Paper>
        }
        PopperProps={{
          sx: {
            "& .MuiTooltip-tooltip": {
              backgroundColor: "#ffffff",
              color: "#000000",
              m: 0,
              p: 0,
            },
          },
        }}
        open={pointsOpen || visualOpen}
        followCursor
        {...rest}
      >
        {isVisualControlOpen ? (
          isSplitVisualCell ? (
            <Box
              onClick={handleCellClick}
              sx={{
                cursor: pointsStatus && pointsStatus > 30 ? "pointer" : "default",
                position: "relative",
                width: 62,
                height: 62,
              }}
            >
              <Box
                onMouseOver={handlePointsMouseOver}
                onMouseOut={onPointsMouseOut}
                onClick={handleCellClick}
                sx={{
                  border: "solid transparent",
                  boxSizing: "border-box",
                  borderWidth: 62 / 2,
                  borderRightColor: pointsColor,
                  borderTopColor: pointsColor,
                  position: "absolute",
                  height: 62,
                  width: 62,
                }}
              />
              <Box
                onMouseOver={handleVisualMouseOverTrain}
                onMouseOut={onVisualMouseOutTrain}
                onClick={handleCellClick}
                sx={{
                  border: "solid transparent",
                  boxSizing: "border-box",
                  borderWidth: 62 / 2,
                  borderLeftColor:
                    valueType === 2
                      ? "#E0E0E0"
                      : commentTypeSymbol === VISUAL_COMMENT_TYPE_SYMBOL.text || !visualStatus || visualStatus === 0
                        ? "#E0E0E0"
                        : visualColor,
                  borderBottomColor:
                    valueType === 2
                      ? "#E0E0E0"
                      : commentTypeSymbol === VISUAL_COMMENT_TYPE_SYMBOL.text || !visualStatus || visualStatus === 0
                        ? "#E0E0E0"
                        : visualColor,
                  position: "absolute",
                  height: 62,
                  width: 62,
                }}
              />
            </Box>
          ) : (
            <Box
              onMouseOver={handleVisualMouseOverPoints}
              onMouseOut={onVisualMouseOutPoints}
              onClick={handleCellClick}
              sx={{
                cursor:
                  commentTypeSymbol || (pointsStatus && pointsStatus > 30 && props.row && props.row.name)
                    ? "pointer"
                    : "default",
                backgroundColor: pointsColor,
                fontSize: "24px",
                color: "#000",
                position: "relative",
                width: "100%",
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {commentTypeSymbol}
            </Box>
          )
        ) : (
          <Box
            onClick={handleCellClick}
            onMouseOver={handlePointsMouseOver}
            onMouseOut={onPointsMouseOut}
            sx={{
              cursor: pointsStatus && pointsStatus > 30 ? "pointer" : "default",
              width: "100%",
              height: "100%",
              backgroundColor: pointsColor,
              position: "relative",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          />
        )}
      </Tooltip>
      {(openCommentListDialog || isLoadingDialogData) && (
        <CommentListDialog
          open={openCommentListDialog}
          onClose={handleCommentListDialogClose}
          data={commentListDialogData}
          onRowClick={handleDialogRowClick}
          loading={isLoadingDialogData}
        />
      )}
      {(openCommentDetailsDialog || commentDetailsDialogData) && (
        <CommentDetailsDialog
          open={openCommentDetailsDialog}
          onClose={handleCommentDetailsDialogClose}
          data={commentDetailsDialogData}
          loading={isLoadingDialogData}
        />
      )}
    </>
  );
};
